/**
 * Audit UI Phase 1 — 분류사례 전용 AUI Grid (ResultsPage 안에 embed).
 *
 * ResultSection 대신 사용. virtual scroll + sort/filter + 더블클릭 상세 패널.
 * page_size 가 크므로 chapter 전체 (~max 1,615 in ch85) 한 번에 로드.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import SourceEvidenceLink, { precedentEvidence } from "./SourceEvidenceLink";
import "./theme.css";

const COLUMN_LAYOUT = [
  {
    dataField: "decided_hs_code_6digit",
    headerText: "HS 6자리",
    width: 90,
    style: "audit-grid-mono",
  },
  {
    dataField: "decided_hsk_10digit",
    headerText: "HSK 10자리",
    width: 110,
    style: "audit-grid-mono",
  },
  { dataField: "decision_date", headerText: "시행일", width: 100 },
  { dataField: "source", headerText: "출처", width: 130 },
  {
    dataField: "case_id",
    headerText: "사례번호",
    width: 200,
    style: "audit-grid-mono",
  },
  { dataField: "title_ko", headerText: "품명", width: "100%", minWidth: 240 },
];

const GRID_PROPS = {
  width: "100%",
  height: "100%",
  showRowNumColumn: true,
  rowNumHeaderText: "No",
  rowNumWidth: 40,
  showStateColumn: false,
  editable: false,
  selectionMode: "singleRow",
  enableSorting: true,
  enableFilter: true,
  enableMovingColumn: true,
  enableColumnResize: true,
  rowIdField: "case_id",
  rowHeight: 28,
  headerHeight: 32,
  showFooter: false,
  pageRowCount: 100,
  showPageRowSelect: false,
  showCheckedRowOnly: false,
  showRowBorder: true,
  showColumnBorder: true,
};

let _gridIdCounter = 0;

export default function PrecedentAuiGrid({
  rows = [],
  loading = false,
  total = 0,
  highlighted = new Set(),
  title = "분류사례",
}) {
  const gridId = useMemo(() => `audit-precedent-grid-${++_gridIdCounter}`, []);
  const gridDivRef = useRef(null);
  const gridReadyRef = useRef(false);
  const [selected, setSelected] = useState(null);

  // AUI Grid 초기화 (1회)
  useEffect(() => {
    if (!window.AUIGrid || !gridDivRef.current) {
      console.warn("[PrecedentAuiGrid] AUIGrid not loaded");
      return;
    }
    const selector = `#${gridId}`;
    if (window.AUIGrid.isCreated(selector)) {
      window.AUIGrid.destroy(selector);
    }
    window.AUIGrid.create(selector, COLUMN_LAYOUT, GRID_PROPS);
    gridReadyRef.current = true;

    window.AUIGrid.bind(selector, "cellDoubleClick", (e) => {
      const row = e && e.item;
      if (row && row.case_id) setSelected(row);
    });

    return () => {
      if (window.AUIGrid && window.AUIGrid.isCreated(selector)) {
        window.AUIGrid.destroy(selector);
      }
      gridReadyRef.current = false;
    };
  }, [gridId]);

  // rows 가 바뀌면 그리드에 반영. AI 추천 ★ 표시는 사례번호 prefix 로
  useEffect(() => {
    if (!gridReadyRef.current || !window.AUIGrid) return;
    const selector = `#${gridId}`;
    if (window.AUIGrid.isCreated(selector)) {
      const enriched = rows.map((r) => ({
        ...r,
        case_id: highlighted.has(r.case_id) ? `★ ${r.case_id}` : r.case_id,
      }));
      window.AUIGrid.setGridData(selector, enriched);
    }
  }, [rows, gridId, highlighted]);

  return (
    <section
      className="audit-root"
      style={{
        marginBottom: 16,
        border: "1px solid var(--audit-border)",
        borderRadius: 6,
        background: "var(--audit-bg-elevated)",
        overflow: "hidden",
      }}
    >
      <header
        style={{
          padding: "10px 14px",
          borderBottom: "1px solid var(--audit-border)",
          background: "var(--audit-bg-subtle)",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span
          style={{ fontSize: 13, fontWeight: 600, color: "var(--audit-text)" }}
        >
          {title}
        </span>
        <span
          style={{
            fontSize: 12,
            color: "var(--audit-text-muted)",
            fontFamily: "JetBrains Mono, monospace",
          }}
        >
          ({(total ?? rows.length).toLocaleString()})
        </span>
        {loading && (
          <span
            style={{
              marginLeft: "auto",
              fontSize: 12,
              color: "var(--audit-text-faint)",
            }}
          >
            로딩...
          </span>
        )}
        {!loading && rows.length > 0 && (
          <span
            style={{
              marginLeft: "auto",
              fontSize: 12,
              color: "var(--audit-text-faint)",
            }}
          >
            정렬·필터·더블클릭으로 상세 보기 · 표시{" "}
            {rows.length.toLocaleString()}/
            {(total ?? rows.length).toLocaleString()}
          </span>
        )}
      </header>

      <div style={{ position: "relative", height: 480 }}>
        <div
          id={gridId}
          ref={gridDivRef}
          style={{ width: "100%", height: "100%" }}
        />
        {loading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,0.55)",
              zIndex: 10,
            }}
          >
            <span style={{ color: "var(--audit-text-muted)", fontSize: 12 }}>
              로딩...
            </span>
          </div>
        )}
        {!loading && rows.length === 0 && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--audit-text-muted)",
              fontSize: 12,
              fontStyle: "italic",
            }}
          >
            분류사례 결과 없음
          </div>
        )}
      </div>

      {/* 상세 슬라이드 패널 */}
      {selected && (
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            width: 480,
            background: "var(--audit-bg-elevated)",
            borderLeft: "1px solid var(--audit-border-strong)",
            padding: 24,
            overflowY: "auto",
            boxShadow: "-4px 0 16px rgba(60,64,67,0.15)",
            zIndex: 50,
            color: "var(--audit-text)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start",
              marginBottom: 12,
            }}
          >
            <h2
              style={{
                fontSize: 16,
                margin: 0,
                fontFamily: "JetBrains Mono, monospace",
              }}
            >
              {(selected.case_id || "").replace(/^★ /, "")}
            </h2>
            <button
              className="audit-btn"
              onClick={() => setSelected(null)}
              style={{ padding: 4 }}
            >
              <X size={14} />
            </button>
          </div>

          <table style={{ fontSize: 12, width: "100%", marginBottom: 16 }}>
            <tbody>
              <tr>
                <td
                  style={{
                    color: "var(--audit-text-muted)",
                    paddingRight: 12,
                  }}
                >
                  HSK
                </td>
                <td style={{ fontFamily: "JetBrains Mono, monospace" }}>
                  {selected.decided_hsk_10digit ||
                    selected.decided_hs_code_6digit}
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    color: "var(--audit-text-muted)",
                    paddingRight: 12,
                  }}
                >
                  시행일
                </td>
                <td>{selected.decision_date}</td>
              </tr>
              <tr>
                <td
                  style={{
                    color: "var(--audit-text-muted)",
                    paddingRight: 12,
                  }}
                >
                  출처
                </td>
                <td>{selected.source}</td>
              </tr>
              <tr>
                <td style={{ color: "var(--audit-text-muted)", paddingRight: 12 }}>근거</td>
                <td>
                  <SourceEvidenceLink
                    compact
                    {...precedentEvidence({
                      decisionUrl: selected.decision_url,
                      caseId: selected.case_id,
                      source: selected.source,
                      code: selected.decided_hsk_10digit || selected.decided_hs_code_6digit,
                      refNo: selected.ref_no,
                      officialForeignSearch: true,
                    })}
                  />
                </td>
              </tr>
            </tbody>
          </table>

          <h3
            style={{
              fontSize: 13,
              color: "var(--audit-text-muted)",
              textTransform: "uppercase",
              letterSpacing: 0.5,
              marginBottom: 6,
            }}
          >
            품명
          </h3>
          <div style={{ marginBottom: 16, fontSize: 13 }}>
            {selected.title_ko}
          </div>

          {selected.description_ko && (
            <>
              <h3
                style={{
                  fontSize: 13,
                  color: "var(--audit-text-muted)",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  marginBottom: 6,
                }}
              >
                물품 설명
              </h3>
              <div
                style={{
                  marginBottom: 16,
                  fontSize: 12,
                  color: "var(--audit-text-muted)",
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap",
                }}
              >
                {selected.description_ko}
              </div>
            </>
          )}

          {selected.key_reasoning_excerpt && (
            <>
              <h3
                style={{
                  fontSize: 13,
                  color: "var(--audit-text-muted)",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  marginBottom: 6,
                }}
              >
                결정 사유
              </h3>
              <div
                style={{
                  fontSize: 12,
                  color: "var(--audit-text)",
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap",
                }}
              >
                {selected.key_reasoning_excerpt}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
